'use client';

import { useState, useEffect, useTransition } from 'react';
import { Drawer } from '../../../../common/components/Drawer/Drawer';
import {
  DrawerComponent,
  useDrawerMetadata,
  useModalDrawerContext,
} from '../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { getFiatWalletWithAccountInfo } from '../../../../common/gqlActions/getFiatWalletWithAccountInfo.action';
import { getFiatTransactionFee } from '../../../../common/gqlActions/getFiatTransactionFee.action';
import { WithdrawalAmountDrawer } from '../WithdrawalAmount/WithdrawalAmountDrawer';
import { WithdrawalMfaDrawer } from '../WithdrawalMfa/WithdrawalMfaDrawer';
import { WithdrawalSummaryContent } from './WithdrawalSummaryContent';
import { WithdrawalSummarySkeleton } from './WithdrawalSummarySkeleton';
import {
  datadogErrorHelper,
  useHandleGqlErrorsWithSnackbar,
} from '@bts-web/core-features/common';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';

interface WithdrawalSummaryDrawerProps {
  amount: string;
  showFee?: boolean;
  tradeId?: string;
}

type FiatWallet = {
  bankAccountHolder: string;
  accountNumber: string;
  bankName: string;
  payoutAccountId: string;
};

const WithdrawalSummaryDrawer: DrawerComponent<
  WithdrawalSummaryDrawerProps
> = ({ amount, showFee, tradeId }) => {
  const { t } = useClientTranslation();

  const { closeThisDrawer } = useDrawerMetadata();

  const { openDrawer } = useModalDrawerContext();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const [isLoading, startTransition] = useTransition();

  const [fiatWallet, setFiatWallet] = useState<FiatWallet | null>(null);

  const [fee, setFee] = useState<string | null>(null);

  useEffect(() => {
    startTransition(async () => {
      try {
        const [walletResponse, feeResponse] = await Promise.all([
          getFiatWalletWithAccountInfo(),
          showFee
            ? getFiatTransactionFee({ amount: amount, type: 'WITHDRAWAL' })
            : Promise.resolve(null),
        ]);

        if (walletResponse.errors) {
          processErrors(walletResponse.errors);
        }

        setFiatWallet(walletResponse.data);

        if (feeResponse) {
          if (feeResponse.errors) {
            processErrors(feeResponse.errors);
          }

          setFee(feeResponse.data);
        }
      } catch (error) {
        closeThisDrawer();

        datadogErrorHelper({
          errorMessage:
            error instanceof Error ? error.message : 'Unknown error',
          errorSeverity: DatadogErrorLevels.HIGH,
        });

        processErrors([t('general_error_message_title')]);
      }
    });
  }, []);

  const handleBack = () => {
    closeThisDrawer();

    openDrawer(WithdrawalAmountDrawer);
  };

  const handleConfirm = ({
    amount: confirmedAmount,
    payoutAccountId,
    recipientName,
  }: {
    amount: string;
    payoutAccountId: string;
    recipientName: string;
  }) => {
    closeThisDrawer();

    openDrawer(WithdrawalMfaDrawer, {
      amount: confirmedAmount,
      payoutAccountId,
      recipientName,
      tradeId,
    });
  };

  return (
    <Drawer hideDefaultHeader>
      {!isLoading && fiatWallet ? (
        <WithdrawalSummaryContent
          amount={amount}
          payoutAccountId={fiatWallet.payoutAccountId}
          recipientName={fiatWallet.bankAccountHolder}
          bankName={fiatWallet.bankName}
          accountNumber={fiatWallet.accountNumber}
          fee={fee}
          onBack={handleBack}
          onConfirm={handleConfirm}
        />
      ) : (
        <WithdrawalSummarySkeleton />
      )}
    </Drawer>
  );
};

export { WithdrawalSummaryDrawer };

export type { WithdrawalSummaryDrawerProps };
